// useBookTime.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { getCart, selectCart } from "app/redux/cartSlice";
import { Schedule } from "app/redux/schedulesSlice";
import { getAccount, selectAccount } from "app/redux/accountSlice";
import { useQuery } from "app/utils/useQuery";
import { Path } from "app/path";
import { format, isSameDay, addMinutes, addDays, startOfDay } from "date-fns";
import cable from "app/cable";
import { getAppointmentGroupByBookingToken } from "app/redux/appointmentGroupSlice";
import { toLocalDate, localDateToUtc, setActiveShopTimeZone } from "app/utils/timezone";
import { DualPricingPaymentTypes } from "../useBook";
import { ModalTypes } from "app/order/useOrder";
import { TimeSlot } from "../../types";
import { parseTime, formatTime, generateTimeSlots, formatTimeToNearestInterval } from "../utils/timeSlots";
import { getEmployeeSchedulesForDates, findAnyEmployeeAvailableTimeSlots, overlapsAnyOnDate } from "../utils/availability";
import { getServiceProviders } from "../utils/providers";
import { useOrderFinalizer } from "../utils/useOrderFinalizer";

enum CartStatus {
  COMPLETED = "completed",
  PENDING = "pending",
}

// Find available time slots (unchanged)
// const findAvailableTimeSlotsForDate = (
//   schedules: { [employeeId: number]: { [date: string]: Schedule } },
//   cartServices: any[],
//   date: Date,
//   interval: number,
//   currentDate: Date,
//   getTimeSlotsForReschedule: () => any[]
// ): string[] => {
//   const dateString = date.toISOString().split("T")[0];
//   const isSameDayFlag = isSameDay(currentDate, date);
//
//   const employeeTimeSlots: { [employeeId: number]: string[] } = {};
//
//   for (const service of cartServices) {
//     const employeeId = service.employee.id;
//     const schedule = schedules[employeeId]?.[dateString];
//
//     if (!schedule || !schedule.availability) {
//       console.warn(
//         `No availability for employee ${employeeId} on ${dateString}`
//       );
//       return [];
//     }
//
//     const { availability, appointments, blockedTimes } = schedule;
//
//     // Generate all possible time slots based on the availability
//     const availableTimeSlots = generateTimeSlots(
//       availability.timeStart,
//       availability.timeEnd,
//       interval
//     );
//
//     // Filter time slots to remove those that overlap with appointments or block times
//     const filteredTimeSlots = availableTimeSlots.filter((slot) => {
//       const [slotStartStr] = slot.split(" - ");
//       const slotStartTime = parseTime(slotStartStr);
//
//       // Filter out slots in the past if it's the same day
//       if (
//         isSameDayFlag &&
//         slotStartTime < currentDate.getHours() * 60 + currentDate.getMinutes()
//       ) {
//         return false;
//       }
//
//       // Check for overlaps with appointments
//       for (const appointment of appointments) {
//         const appointmentStartTime = parseTime(
//           appointment.startTime.slice(11, 16)
//         );
//         const appointmentEndTime = parseTime(appointment.endTime.slice(11, 16));
//         if (
//           slotStartTime < appointmentEndTime &&
//           slotStartTime + interval > appointmentStartTime
//         ) {
//           return false;
//         }
//       }
//
//       for (const blockTime of blockedTimes) {
//         const blockStartTime = parseTime(blockTime.startTime.slice(11, 16));
//         const blockEndTime = parseTime(blockTime.endTime.slice(11, 16));
//
//         if (
//           slotStartTime < blockEndTime &&
//           slotStartTime + interval > blockStartTime
//         ) {
//           return false;
//         }
//       }
//
//       return true;
//     });
//
//     employeeTimeSlots[employeeId] = filteredTimeSlots.map(
//       (slot) => slot.split(" - ")[0]
//     );
//   }
//
//
//
//
//
//   // Find common slots where all services can be scheduled consecutively
//   const firstEmployeeId = cartServices[0].employee.id;
//   const firstEmployeeSlots = employeeTimeSlots[firstEmployeeId] || [];
//
//   const serviceDurations = cartServices.map((service) => service.duration);
//
//   const availableTimeSlots: string[] = [];
//
//   for (const startTimeStr of firstEmployeeSlots) {
//     let canScheduleAll = true;
//     let currentTime = parseTime(startTimeStr);
//
//     for (let i = 0; i < cartServices.length; i++) {
//       const service = cartServices[i];
//       const employeeId = service.employee.id;
//       const employeeSlots = employeeTimeSlots[employeeId] || [];
//       const serviceDuration = serviceDurations[i];
//
//       // Check if all slots required for the service duration are available
//       for (
//         let durationCheckTime = currentTime;
//         durationCheckTime < currentTime + serviceDuration;
//         durationCheckTime += interval // Move in 15-minute intervals
//       ) {
//         const alignedSlotStr = formatTimeToNearestInterval(
//           formatTime(durationCheckTime),
//           interval
//         );
//         if (!employeeSlots.includes(alignedSlotStr)) {
//           canScheduleAll = false;
//           break;
//         }
//       }
//
//       if (!canScheduleAll) {
//         break;
//       }
//
//       // Move current time forward for the next service
//       currentTime += serviceDuration;
//     }
//
//     if (canScheduleAll) {
//       availableTimeSlots.push(startTimeStr);
//     }
//   }
//
//   const rescheduleSlots = getTimeSlotsForReschedule();
//   rescheduleSlots.forEach((slot) => {
//     if (!availableTimeSlots.includes(slot)) {
//       availableTimeSlots.push(slot);
//     }
//   });
//   availableTimeSlots.sort(); // Sort to maintain order
//
//   return availableTimeSlots;
// };

// Find available time slots
const findAvailableTimeSlotsForDate = (
  schedules: { [employeeId: number]: { [date: string]: Schedule } },
  cartServices: any[],
  date: Date,
  interval: number,
  currentDate: Date,
  getTimeSlotsForReschedule: () => any[]
): string[] => {
  const dateString = format(date, "yyyy-MM-dd");
  const isSameDayFlag = isSameDay(currentDate, date);

  const employeeTimeSlots: { [employeeId: number]: string[] } = {};

  for (const service of cartServices) {
    const employeeId = service.employee.id;
    const schedule = schedules[employeeId]?.[dateString];

    if (!schedule || !schedule.availability) {
      console.warn(
        `No availability for employee ${employeeId} on ${dateString}`
      );
      return [];
    }

    // 1. Destructure appointmentGroups as well
    const { availability, appointments, blockedTimes, appointmentGroups } = schedule as any;

    // 2. Combine direct appointments with appointments nested inside groups
    const groupAppointments = appointmentGroups
      ? appointmentGroups.flatMap((group: any) => group.appointments || [])
      : [];

    const allAppointments = [...appointments, ...groupAppointments];

    // Generate all possible time slots based on the availability
    const availableTimeSlots = generateTimeSlots(
      availability.timeStart,
      availability.timeEnd,
      interval
    );
    const filteredTimeSlots = availableTimeSlots.filter((slot) => {
      const [slotStartStr] = slot.split(" - ");
      const slotStartTime = parseTime(slotStartStr);

      // past-time filter
      if (
        isSameDayFlag &&
        slotStartTime <
        currentDate.getHours() * 60 + currentDate.getMinutes()
      ) {
        return false;
      }

      // appointments and blocked times on this date only (shop-local)
      const slotEndTime = slotStartTime + interval;
      return (
        !overlapsAnyOnDate(allAppointments, dateString, slotStartTime, slotEndTime) &&
        !overlapsAnyOnDate(blockedTimes, dateString, slotStartTime, slotEndTime)
      );
    });


    employeeTimeSlots[employeeId] = filteredTimeSlots.map(
      (slot) => slot.split(" - ")[0]
    );
  }

  // Find common slots where all services can be scheduled consecutively
  const firstEmployeeId = cartServices[0].employee.id;
  const firstEmployeeSlots = employeeTimeSlots[firstEmployeeId] || [];

  const serviceDurations = cartServices.map((service) => service.duration);

  const availableTimeSlots: string[] = [];

  for (const startTimeStr of firstEmployeeSlots) {
    let canScheduleAll = true;
    let currentTime = parseTime(startTimeStr);

    for (let i = 0; i < cartServices.length; i++) {
      const service = cartServices[i];
      const employeeId = service.employee.id;
      const employeeSlots = employeeTimeSlots[employeeId] || [];
      const serviceDuration = serviceDurations[i];

      // Check if all slots required for the service duration are available
      for (
        let durationCheckTime = currentTime;
        durationCheckTime < currentTime + serviceDuration;
        durationCheckTime += interval // Move in 15-minute intervals
      ) {
        const alignedSlotStr = formatTimeToNearestInterval(
          formatTime(durationCheckTime),
          interval
        );
        if (!employeeSlots.includes(alignedSlotStr)) {
          canScheduleAll = false;
          break;
        }
      }

      if (!canScheduleAll) {
        break;
      }

      // Move current time forward for the next service
      currentTime += serviceDuration;
    }

    if (canScheduleAll) {
      availableTimeSlots.push(startTimeStr);
    }
  }

  const rescheduleSlots = getTimeSlotsForReschedule();
  rescheduleSlots.forEach((slot) => {
    if (!availableTimeSlots.includes(slot)) {
      availableTimeSlots.push(slot);
    }
  });
  availableTimeSlots.sort(); // Sort to maintain order

  return availableTimeSlots;
};




export const useBookTime = () => {
  const { nameKey, shopKey }: any = useParams();
  const query = useQuery();
  const cartUid = query.get("cart");
  const from = query.get("from");
  const account = useSelector(selectAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);

  // Entering straight from service selection (no cart yet): the calendar must
  // show availability across every eligible provider instead of one already picked.
  const serviceIdParam = query.get("serviceId");
  const isFreshFlow = !cartUid && !!serviceIdParam;

  const shop = useMemo(() => {
    if (cart?.shop) return cart.shop;
    return account?.shops?.find((s: any) => s.key === shopKey);
  }, [cart, account, shopKey]);
  const timezone = shop?.timezone;

  useEffect(() => {
    setActiveShopTimeZone(timezone?.timeZoneId);
  }, [timezone?.timeZoneId]);

  const eligibleEmployeeIds = useMemo(() => {
    if (!isFreshFlow || !account?.shops || !serviceIdParam) return [];
    try {
      return getServiceProviders({ account, shopKey, serviceId: serviceIdParam }).map(
        (sp: any) => sp.id
      );
    } catch {
      return [];
    }
  }, [isFreshFlow, account, shopKey, serviceIdParam]);

  // The service being booked, before any cart exists - used to show its
  // name/duration/price on this screen while isFreshFlow.
  const freshService = useMemo(() => {
    if (!isFreshFlow || !account?.services) return undefined;
    return account.services.find((s: any) => s.id === Number(serviceIdParam));
  }, [isFreshFlow, account, serviceIdParam]);

  const freshServiceDuration = freshService?.duration || 0;

  const { showDualPricingModal, setShowDualPricingModal, onDualPricingPayBy, navigateToOrderUrl, finalizeCartWithTime } =
    useOrderFinalizer({ nameKey, shopKey, account, query });

  // "Now" as it reads on the shop's wall clock, not the visitor's device clock -
  // drives which slots count as "already passed" and which weekday is "today".
  // The zone is passed in: the effect above that sets the shared one runs after this first render, so
  // the first value used to come from the visitor's own zone.
  const currentShopTime = useMemo(() => {
    return toLocalDate(new Date().toISOString(), timezone?.timeZoneId);
  }, [timezone]);

  // The month grid shown on screen - independent of which date is selected or
  // fetched; only decides which day cells are rendered.
  const [currentMonth, setCurrentMonth] = useState(() => currentShopTime);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay());

    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [currentMonth]);

  const [isLoading, setIsLoading] = useState(false);
  const [cartServices, setCartServices] = useState<any[]>([]);
  // Re-landing here via the back button from provider selection: restore the slot they'd picked.
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    const dateParam = query.get("date");
    return dateParam ? new Date(`${dateParam}T00:00:00`) : currentShopTime;
  });
  // Until the visitor picks a day, the selected day is the shop's today - which can change once the
  // shop (and its zone) has loaded. From India in the morning it used to preselect the shop's tomorrow.
  const datePicked = useRef(!!query.get("date"));
  const pickDate = useCallback((date: Date | undefined) => {
    datePicked.current = true;
    setSelectedDate(date);
  }, []);
  useEffect(() => {
    if (!datePicked.current) setSelectedDate(currentShopTime);
  }, [currentShopTime]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | undefined>(
    () => query.get("time") || undefined
  );
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);

  const [timeSlots, setTimeSlots] = useState<{
    [date: string]: string[];
  }>({});

  const [appointmentGroupToReschedule, setAppointmentGroupToReschedule] =
    useState<any>(null);

  const navigateToService = () => {
    const serviceUrl = generatePath(Path.BOOK_SERVICE, {
      nameKey,
      shopKey,
    } as any);

    navigate(`${serviceUrl}?${query.toString()}`);
  };

  const navigateToStaff = () => {
    const staffUrl = generatePath(Path.CART_INIT, {
      nameKey,
      shopKey,
      serviceId: cart?.merchantServices[0]?.businessService?.id,
      serviceProviderId: cart?.merchantServices[0]?.employee?.id
    } as any);

    navigate(`${staffUrl}?${query.toString()}`);
  };




  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() - 1);
      return next;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  };

  const updateCartWithTime = async () => {
    try {
      if (!selectedDate || !selectedTimeSlot) {
        console.warn("Selected date or time slot is missing.");
        return;
      }

      const [hours, minutes] = selectedTimeSlot.split(":").map(Number);
      const combinedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        hours,
        minutes
      );
      const utcTime = localDateToUtc(combinedDate);

      await finalizeCartWithTime({ cartUid, startTime: utcTime });
    } catch (err) {
      console.error("Error updating cart with time:", err);
    }
  };

  // Fresh flow (service picked, no provider/cart yet): stash the chosen slot in
  // the URL and move on to picking from whoever is actually free at that time.
  const confirmTimeSelection = () => {
    if (!selectedDate || !selectedTimeSlot || !serviceIdParam) {
      console.warn("Selected date or time slot is missing.");
      return;
    }

    const staffUrl = generatePath(Path.BOOK_STAFF, {
      nameKey,
      shopKey,
      serviceId: serviceIdParam,
    } as any);

    query.set("date", format(selectedDate, "yyyy-MM-dd"));
    query.set("time", selectedTimeSlot);

    navigate(`${staffUrl}?${query.toString()}`);
  };

  // Initialize: fetch account and, for the "edit an existing cart's time" case, cart data
  const init = async () => {
    const fallBackUrl = generatePath(Path.BOOK_SERVICE, {
      nameKey,
      shopKey,
    });

    if (!cartUid) {
      if (!isFreshFlow) {
        return navigate(fallBackUrl);
      }

      try {
        setIsLoading(true);
        await dispatch(getAccount({ nameKey }) as any).unwrap();
      } catch (error) {
        console.error("Initialization error:", error);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      await dispatch(getAccount({ nameKey }) as any).unwrap();
      const { merchantServices, status } = await dispatch(
        getCart({ uid: cartUid }) as any
      ).unwrap();

      if (status === CartStatus.COMPLETED) {
        if (from === "bookService") return;
        return navigate(fallBackUrl);
      }

      setCartServices(merchantServices);
    } catch (error) {
      console.error("Initialization error:", error);
    } finally {
      setIsLoading(false);
    }

    if (query.get("appointmentGroupToReschedule")) {
      try {
        const appointmentGroup = await dispatch(
          getAppointmentGroupByBookingToken({
            bookingToken: query.get("appointmentGroupToReschedule"),
          } as any) as any
        ).unwrap();
        if (appointmentGroup) {
          setAppointmentGroupToReschedule(appointmentGroup);
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error(error);
        navigate("/");
      }
    }
  };

  const getTimeSlotsForReschedule = () => {
    if (appointmentGroupToReschedule && selectedDate) {
      const rescheduleDate = toLocalDate(
        appointmentGroupToReschedule.startTime
      );
      const isSameDayReschedule = isSameDay(rescheduleDate, selectedDate);

      if (isSameDayReschedule) {
        const rescheduleStartTime = format(rescheduleDate, "HH:mm");
        const rescheduleEndTime = format(
          addMinutes(rescheduleDate, appointmentGroupToReschedule.duration),
          "HH:mm"
        );

        // Use generateTimeSlots to split into 15-minute increments
        return generateTimeSlots(
          rescheduleStartTime,
          rescheduleEndTime,
          15
        ).map((slot) => slot.split(" - ")[0]);
      }
    }
    return [];
  };



  //
  // useEffect(() => {
  //   const nextAvailableDate = daysOfWeek.find((date) => {
  //     const dateKey = date.toISOString().split("T")[0];
  //     return datesAvailability[dateKey];
  //   });
  //
  //   if (nextAvailableDate) {
  //     setSelectedDate(nextAvailableDate);
  //   }
  // }, [datesAvailability, currentWeekStart]);

  // Fetch data on mount
  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to schedule updates via ActionCable (assuming Rails backend with ActionCable)
  useEffect(() => {
    if (!cart.shop) return;
    const subscription = cable.subscriptions.create(
      {
        channel: "SchedulesChannel",
        accountId: cart.shop.account.id,
      },
      {
        connected: () => { },
        disconnected: () => { },
        received: (data) => {
          init();
        },
      }
    );

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.shop]);

  // **Modified useEffect to Handle Availability and Next Week Check**
  // useEffect(() => {
  //   const fetchAvailabilityData = async () => {
  //     if (cartServices.length === 0) return;
  //     setIsLoading(true);
  //
  //     try {
  //       const employeeIds = Array.from(
  //         new Set(cartServices.map((service) => service.employee.id))
  //       );
  //
  //       const datesToFetch = daysOfWeek;
  //
  //       // Fetch schedules for all employees and dates
  //       const schedules = await getEmployeeSchedulesForDates(
  //         dispatch,
  //         employeeIds,
  //         datesToFetch,
  //         nameKey
  //       );
  //
  //       const availabilityMap: { [key: string]: boolean } = {};
  //       const timeSlotsMap: { [key: string]: string[] } = {};
  //
  //       for (const date of datesToFetch) {
  //         const availableTimeSlots = findAvailableTimeSlotsForDate(
  //           schedules,
  //           cartServices,
  //           date,
  //           15, // interval in minutes
  //           currentShopTime,
  //           getTimeSlotsForReschedule
  //         );
  //
  //         const dateKey = date.toISOString().split("T")[0];
  //
  //         availabilityMap[dateKey] =
  //           availableTimeSlots.length > 0 && date >= startOfDay(new Date());
  //
  //         timeSlotsMap[dateKey] = availableTimeSlots;
  //       }
  //
  //       const hasAvailability = Object.values(availabilityMap).some(Boolean);
  //
  //       if (!hasAvailability && !hasCheckedNextWeek) {
  //         // No availability in current week, check next week
  //         const nextWeekStart = addDays(currentWeekStart, 7);
  //         setCurrentWeekStart(nextWeekStart);
  //         setHasCheckedNextWeek(true); // Prevent further checks
  //         return; // Exit to allow useEffect to re-run with updated week
  //       }
  //
  //       // If availability is found, update the state
  //       setDatesAvailability(availabilityMap);
  //       setWeekTimeSlots(timeSlotsMap);
  //     } catch (error) {
  //       console.error("Error fetching availability data:", error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };
  //
  //   fetchAvailabilityData();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [cartServices, currentWeekStart, hasCheckedNextWeek]);


  useEffect(() => {
    const fetchAvailabilityData = async () => {
      const employeeIds = isFreshFlow
        ? eligibleEmployeeIds
        : Array.from(new Set(cartServices.map((service) => service.employee.id)));

      if (employeeIds.length === 0) return;
      if (isFreshFlow && freshServiceDuration === 0) return;

      setIsLoading(true);

      try {
        const dateToFetch = selectedDate ?? currentShopTime;

        const schedules = await getEmployeeSchedulesForDates(
          dispatch,
          employeeIds,
          dateToFetch,
          nameKey
        );

        const availableTimeSlots = isFreshFlow
          ? findAnyEmployeeAvailableTimeSlots({
              schedules,
              employeeIds,
              duration: freshServiceDuration,
              date: dateToFetch,
              interval: 15,
              currentDate: currentShopTime,
            })
          : findAvailableTimeSlotsForDate(
              schedules,
              cartServices,
              dateToFetch,
              15, // interval in minutes
              currentShopTime,
              getTimeSlotsForReschedule
            );

        const dateKey = format(dateToFetch, "yyyy-MM-dd");

        setTimeSlots((prev) => ({ ...prev, [dateKey]: availableTimeSlots }));
      } catch (error) {
        console.error("Error fetching availability data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailabilityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreshFlow, cartServices, eligibleEmployeeIds, freshServiceDuration, selectedDate]);




  // Update availableTimeSlots when selectedDate changes
  useEffect(() => {

    console.log('date is', selectedDate)

    if (selectedDate) {
      const dateKey = format(selectedDate, "yyyy-MM-dd");
      setAvailableTimeSlots(timeSlots[dateKey] || []);
    }
  }, [selectedDate, timeSlots]);




  return {
    updateCartWithTime,
    confirmTimeSelection,
    isFreshFlow,
    isLoading,
    cartServices,
    selectedDate,
    setSelectedDate: pickDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
    currentShopTime,
    timezone,
    shop,
    cart,
    handlePreviousMonth,
    handleNextMonth,
    calendarDays,
    currentMonth,
    availableTimeSlots,
    navigateToService,
    navigateToStaff,
    account,
    freshService,
    appointmentGroupToReschedule,
    showDualPricingModal,
    setShowDualPricingModal,
    onDualPricingPayBy,
    navigateToOrderUrl
  };
};
